<template>
  <BaseTable
    :isLoading="isLoading"
    :tableData="data"
    :totalItemsCount="totalItemsCount"
    :perPage="perPage"
    :currentPage="currentPage"
    :showSearch="true"
    searchPlaceholder="Search by name"
    :showCreateButton="false"
    @search="onSearch"
    @clear="onClear"
    @paginate="paginate"
  >
    <template #create-button>
      <SplitButton
        v-if="isLTLUser"
        label="Add Rule"
        icon="pi pi-plus"
        :model="splitItems"
        @click="$emit('openModal')"
      />
      <b-button
        v-else
        class="data-table__header--button"
        @click="$emit('openModal')"
      >New Rule</b-button>
    </template>

    <template #filters>
      <Dropdown
        class="data-table__input"
        v-model="selectedOrder"
        :options="orderOptions"
        optionLabel="value"
        placeholder="Order by"
      />
    </template>

    <template #columns>
      <Column field="name" header="Name" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <p>{{ data.name }}</p>
        </template>
      </Column>
      <Column field="label" header="Label" :styles="{ 'min-width': '10rem' }">
        <template #body="{ data }">
          <div class="data-table__chips-container">
            <el-collapse accordion>
              <el-collapse-item name="1">
                <template slot="title">
                  <span
                    v-for="(item, index) in data.label.slice(0, 3)"
                    :key="index"
                    class="data-table__chip"
                  >{{ item }}</span>
                </template>
                <div
                  v-for="(item, index) in data.label.slice(3)"
                  :key="index"
                  class="data-table__chip"
                >{{ item }}</div>
              </el-collapse-item>
            </el-collapse>
          </div>
        </template>
      </Column>
    </template>

    <template #actions-column>
      <Column header="Actions">
        <template #body="{ data }">
          <div class="data-table__actions">
            <span @click="$emit('editRule', data)"><i class="pi pi-pencil"></i></span>
            <span @click="deleteRow(data)"><i class="pi pi-trash"></i></span>
          </div>
        </template>
      </Column>
    </template>
  </BaseTable>
</template>

<script>
import { mapGetters } from "vuex";
import { successMessage, deleteMessage } from "@/utils/alertMessages";
import BaseTable from "./BaseTable.vue";

export default {
  name: "RuleTable",
  components: { BaseTable },
  emits: ["openModal", "editRule"],
  data() {
    return {
      isLoading: false,
      data: [],
      profileActive: null,
      currentCompanyDepartment: null,
      totalItemsCount: 0,
      currentPage: 1,
      perPage: 15,
      isLTLUser: false,
      searchTerm: null,
      selectedOrder: { label: "name", value: "Order by name" },
      orderOptions: [
        { label: "name", value: "Order by name" },
        { label: "rule_weight", value: "Order by rule weight" },
        { label: "profile_id", value: "Order by name profile id" },
      ],
      splitItems: [
        {
          label: "LTL Rule",
          icon: "pi pi-plus",
          command: () => { this.$router.push({ name: "CreateLTLRule" }); },
        },
      ],
    };
  },
  computed: {
    ...mapGetters("user", ["showCurrentCompanyDepartment"]),
  },
  watch: {
    async showCurrentCompanyDepartment() {
      if (this.showCurrentCompanyDepartment) {
        this.currentCompanyDepartment = this.showCurrentCompanyDepartment;
        this.showSplitButton();
        this.currentPage = 1;
        await this.getInfoProfile();
        await this.getAllRules();
      }
    },
    selectedOrder() {
      this.getAllRules();
    },
  },
  async created() {
    this.currentCompanyDepartment = this.showCurrentCompanyDepartment;
    this.showSplitButton();
    await this.getInfoProfile();
    await this.getAllRules();
  },
  methods: {
    showSplitButton() {
      this.isLTLUser = this.showCurrentCompanyDepartment.chosenDepartment.name === "LTL";
    },
    async getInfoProfile() {
      this.isLoading = true;
      const payload = {
        company_id: this.currentCompanyDepartment.chosenCompany.id,
        department_id: this.currentCompanyDepartment.chosenDepartment.id,
      };
      const response = await this.$store.dispatch("company/getProfileCompanysDepartment", payload);
      this.profileActive = response.status === 200 ? response.data : null;
      this.isLoading = false;
    },
    async getAllRules(params) {
      this.isLoading = true;
      const payload = {
        profile_id: this.profileActive?.data[0].profile_id,
        company_id: this.currentCompanyDepartment.chosenCompany.id,
        department_id: this.currentCompanyDepartment.chosenDepartment.id,
        order_by: this.selectedOrder.label,
        limit: 15,
        skip: (this.currentPage - 1) * 15,
      };
      if (params?.name) payload.name = params.name;
      const rulesData = await this.$store.dispatch("rules/getAllRules", payload);
      this.data = rulesData.data;
      this.totalItemsCount = rulesData.count;
      this.isLoading = false;
    },
    async onSearch(term) {
      if (term) {
        this.searchTerm = term;
        this.currentPage = 1;
        await this.getAllRules({ name: term });
      }
    },
    async onClear() {
      this.searchTerm = null;
      this.currentPage = 1;
      await this.getAllRules();
    },
    async paginate(page) {
      this.currentPage = page;
      await this.getInfoProfile();
      await this.getAllRules(this.searchTerm ? { name: this.searchTerm } : undefined);
      window.scrollTo(0, 0);
    },
    async deleteRow(ruleData) {
      const msg = "Are you sure you want to remove Rule from the system?";
      const warningResponse = await deleteMessage(msg, "You won't be able to revert this!");
      if (warningResponse.isConfirmed) {
        const params = {
          company_id: this.currentCompanyDepartment.chosenCompany.id,
          department_id: this.currentCompanyDepartment.chosenDepartment.id,
          profile_id: this.profileActive?.data[0].profile_id,
          rule_id: ruleData.id,
          confirmation_message: "I'm sure I want to delete this",
        };
        const response = await this.$store.dispatch("rules/deleteRule", params);
        if (response?.status === 204) {
          successMessage("Rule has been deleted");
          if (this.data.length === 1 && this.currentPage > 1) this.currentPage -= 1;
          await this.getAllRules();
        }
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@include table-action-icons;

:deep([role="button"]) {
  background-color: transparent;
  line-height: auto;
}
:deep(.el-collapse-item__wrap) {
  background-color: transparent;
}
:deep(.el-collapse-item__header) {
  line-height: 100%;
}
:deep(.p-splitbutton-defaultbutton) {
  @include submit-button;
}
:deep(.p-splitbutton-menubutton) {
  @include submit-button;
  border-top-left-radius: 0;
  border-bottom-left-radius: 0;
}
</style>
